import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import CountryChips from "@/components/CountryChips";
import { useCountry } from "@/lib/CountryContext";
import { formatPrice, billingSuffix } from "@/lib/currency";
import { COUNTRIES } from "@/i18n/countries";
import VendorApprovalBadge from "@/components/VendorApprovalBadge";
import { Plus, ArrowRight } from "lucide-react";

export default function Products() {
    const [data, setData] = useState({ groups: [], deals: [] });
    const [tab, setTab] = useState("deals");
    const [filterCountry, setFilterCountry] = useState("all");
    const navigate = useNavigate();
    const { country } = useCountry();

    // A row matches a country filter if:
    //   • filter is "all", OR
    //   • the row's `country_codes` is empty/null (global — appears in every market), OR
    //   • the row's `country_codes` contains the filter code.
    const matches = (row) => {
        if (filterCountry === "all") return true;
        const codes = row.country_codes;
        if (!codes || codes.length === 0) return true;
        return codes.includes(filterCountry);
    };

    const filteredDeals = useMemo(
        () => data.deals.filter(matches),
        [data.deals, filterCountry]
    );
    const filteredGroups = useMemo(
        () => data.groups.filter(matches),
        [data.groups, filterCountry]
    );

    const load = () =>
        api
            .get("/admin/products")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)));

    useEffect(() => {
        load();
    }, []);

    const pending = data.deals.filter((d) => d.vendor_approval === "pending");

    return (
        <>
            <PageHeader
                eyebrow="Catalogue"
                title="Products & groups"
                description="Every product group and deal on the Perx platform."
                testid="admin-products-header"
                action={
                    <div className="flex items-center gap-3 flex-wrap">
                        {pending.length > 0 && (
                            <Badge
                                data-testid="admin-pending-review-badge"
                                className="bg-gold-500 text-navy-900 border-gold-200 border text-xs px-3 py-1 uppercase tracking-widest"
                            >
                                {pending.length} awaiting vendor
                            </Badge>
                        )}
                        {tab === "groups" ? (
                            <Button
                                data-testid="admin-new-group-btn"
                                onClick={() => navigate("/admin/products/groups/new")}
                                className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                            >
                                <Plus size={16} className="mr-2" /> New group
                            </Button>
                        ) : (
                            <Button
                                data-testid="admin-new-product-btn"
                                onClick={() => navigate("/admin/products/deals/new")}
                                className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6"
                            >
                                <Plus size={16} className="mr-2" /> New product
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="px-6 lg:px-12 py-8">
                <Tabs
                    value={tab}
                    onValueChange={setTab}
                    className="w-full"
                >
                    <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
                        <TabsList className="bg-white border border-navy-100 rounded-full h-11 p-1">
                            <TabsTrigger
                                value="deals"
                                data-testid="admin-tab-deals"
                                className="rounded-full data-[state=active]:bg-navy-700 data-[state=active]:text-white px-5"
                            >
                                {filteredDeals.length} deals
                            </TabsTrigger>
                            <TabsTrigger
                                value="groups"
                                data-testid="admin-tab-groups"
                                className="rounded-full data-[state=active]:bg-navy-700 data-[state=active]:text-white px-5"
                            >
                                {filteredGroups.length} groups
                            </TabsTrigger>
                        </TabsList>

                        <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-widest text-navy-500">
                                Filter by market
                            </span>
                            <Select
                                value={filterCountry}
                                onValueChange={setFilterCountry}
                            >
                                <SelectTrigger
                                    data-testid="admin-products-country-filter"
                                    className="h-10 min-w-[210px] bg-white border-navy-100 rounded-full pl-4"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem
                                        value="all"
                                        data-testid="admin-products-country-filter-all"
                                    >
                                        🌏 All markets
                                    </SelectItem>
                                    {COUNTRIES.map((c) => (
                                        <SelectItem
                                            key={c.code}
                                            value={c.code}
                                            data-testid={`admin-products-country-filter-${c.code}`}
                                        >
                                            {c.flag} {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {filterCountry !== "all" && (
                                <button
                                    data-testid="admin-products-country-filter-clear"
                                    onClick={() => setFilterCountry("all")}
                                    className="text-[11px] uppercase tracking-widest text-gold-600 hover:text-gold-700 underline underline-offset-4"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    <TabsContent value="deals">
                        <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft overflow-hidden">
                            <Table data-testid="admin-deals-table">
                                <TableHeader>
                                    <TableRow className="bg-cream hover:bg-cream">
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Deal</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Vendor</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Group</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Markets</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Price</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Savings</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Rating</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Status</TableHead>
                                        <TableHead className="text-navy-700 uppercase text-xs tracking-[0.2em]">Approval</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredDeals.map((d) => (
                                        <TableRow
                                            key={d.id}
                                            data-testid={`admin-deal-row-${d.id}`}
                                            onClick={() => navigate(`/admin/products/deals/${d.id}`)}
                                            className="cursor-pointer transition-colors hover:bg-cream/60 group"
                                        >
                                            <TableCell className="font-medium text-navy-700 group-hover:text-gold-700 transition-colors">
                                                <span className="inline-flex items-center gap-1.5">
                                                    {d.title}
                                                    <ArrowRight
                                                        size={14}
                                                        className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                                                    />
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-navy-500">{d.vendor}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <Badge className="bg-navy-50 text-navy-700 border-navy-100 border">
                                                        {d.group_id.replace("grp-", "")}
                                                    </Badge>
                                                    {(d.additional_group_ids || []).map((gid) => (
                                                        <Badge
                                                            key={gid}
                                                            data-testid={`deal-extra-group-${d.id}-${gid}`}
                                                            className="bg-white text-navy-500 border-navy-100 border font-normal"
                                                        >
                                                            +{gid.replace("grp-", "")}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <CountryChips
                                                    codes={d.country_codes}
                                                    testid={`deal-countries-${d.id}`}
                                                />
                                            </TableCell>
                                            <TableCell className="text-navy-700">
                                                {formatPrice(d.price, d.is_paid_USD ? "US" : country)}
                                                {billingSuffix(d.billing_interval)}
                                            </TableCell>
                                            <TableCell className="text-gold-600 font-medium">{d.savings_pct}%</TableCell>
                                            <TableCell className="text-navy-500">★ {d.rating.toFixed(1)}</TableCell>
                                            <TableCell>
                                                <StatusBadge
                                                    testid={`deal-status-${d.id}`}
                                                    status={d.status || "active"}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <VendorApprovalBadge
                                                    testid={`deal-approval-${d.id}`}
                                                    status={d.vendor_approval}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="groups">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {filteredGroups.map((g) => {
                                const active = (g.status || "active") === "active";
                                return (
                                    <div
                                        key={g.id}
                                        data-testid={`admin-group-card-${g.id}`}
                                        onClick={() => navigate(`/admin/products/groups/${g.id}`)}
                                        className={`group cursor-pointer rounded-2xl bg-white border border-navy-100/70 shadow-soft p-6 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-lift ${
                                            active ? "" : "opacity-60"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="min-w-0">
                                                <div
                                                    className="text-xs uppercase tracking-[0.3em] mb-2"
                                                    style={{ color: g.accent }}
                                                >
                                                    {g.deal_count} live
                                                </div>
                                                <div className="font-display text-2xl text-navy-700 group-hover:text-gold-700 transition-colors inline-flex items-center gap-1.5">
                                                    {g.name}
                                                    <ArrowRight
                                                        size={14}
                                                        className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                                                    />
                                                </div>
                                                <div className="text-navy-500 text-sm mt-1 line-clamp-1">
                                                    {g.tagline}
                                                </div>
                                            </div>
                                            <img
                                                src={g.image}
                                                alt=""
                                                className="h-16 w-16 object-contain flex-shrink-0"
                                            />
                                        </div>
                                        <div className="mt-4 pt-4 border-t border-navy-100/70 flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="text-[11px] font-mono text-navy-500 truncate">
                                                    {g.id}
                                                </span>
                                                <CountryChips
                                                    codes={g.country_codes}
                                                    testid={`group-countries-${g.id}`}
                                                />
                                            </div>
                                            <StatusBadge
                                                testid={`group-status-${g.id}`}
                                                status={g.status || "active"}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </TabsContent>

                </Tabs>
            </div>

        </>
    );
}

function StatusBadge({ status, testid }) {
    const active = status === "active";
    return (
        <Badge
            data-testid={testid}
            className={
                (active
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-red-50 text-red-700 border-red-200") +
                " border text-[10px] uppercase tracking-widest"
            }
        >
            <span
                className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 ${
                    active ? "bg-emerald-500" : "bg-red-500"
                }`}
            />
            {active ? "Live" : "Hidden"}
        </Badge>
    );
}
